import {Button, DataStatus, FormControl, FormField, IconButton} from 'platform/components';
import {Align, Box, HStack, VStack} from 'platform/foundation';

import {useFieldArray, UseFormReturn} from 'react-hook-form';

import i18n from '@dms/i18n';

import {MESSAGING_ALLOWLIST_LIMITS} from '../constants/MESSAGING_ALLOWLIST_LIMITS';
import {MessagingAllowlistFormSchemaType} from '../types/MessagingAllowlistFormSchemaType';

type PatternListProps = {
  control: FormControl<MessagingAllowlistFormSchemaType>;
  formApi: UseFormReturn<MessagingAllowlistFormSchemaType>;
  channel: 'sms' | 'email';
  isDisabled: boolean;
};

export function PatternList(props: PatternListProps) {
  const name = `${props.channel}.patterns` as const;
  const {fields, append, remove} = useFieldArray({
    control: props.formApi.control,
    name,
  });

  const isAddDisabled =
    props.isDisabled || fields.length >= MESSAGING_ALLOWLIST_LIMITS.MAX_PATTERNS_PER_CHANNEL;

  const handleAddPattern = () => append({value: ''});

  if (fields.length === 0) {
    return (
      <DataStatus
        isEmpty
        spacing={4}
        minHeight="100%"
        action={{
          title: i18n.t('page.demo.messagingAllowlist.addPattern'),
          leftIcon: 'content/add_circle',
          variant: 'link',
          isDisabled: props.isDisabled,
          onClick: handleAddPattern,
        }}
      />
    );
  }

  return (
    <VStack spacing={2}>
      {fields.map((field, index) => (
        <HStack key={field.id} spacing={2} align="flex-end">
          <Box flex={1}>
            <FormField
              control={props.control}
              type="text"
              isDisabled={props.isDisabled}
              name={`${props.channel}.patterns.${index}.value`}
              label={index === 0 ? i18n.t('page.demo.messagingAllowlist.patterns') : undefined}
              data-testid={`messagingAllowlist-${props.channel}-pattern-${index}`}
            />
          </Box>
          <IconButton
            icon="action/delete"
            severity="danger"
            isDisabled={props.isDisabled}
            onClick={() => remove(index)}
            data-testid={`messagingAllowlist-${props.channel}-pattern-${index}-remove`}
          />
        </HStack>
      ))}
      <Align left>
        <Button
          leftIcon="content/add_circle"
          title={i18n.t('page.demo.messagingAllowlist.addPattern')}
          size="small"
          variant="link"
          isDisabled={isAddDisabled}
          onClick={handleAddPattern}
          data-testid={`messagingAllowlist-${props.channel}-pattern-add`}
        />
      </Align>
    </VStack>
  );
}
